import React from 'react';
import { Box, Stack, Typography, alpha } from '@mui/material';
import { getAccentColor } from './shared';

const STAT_THEMES = [
  { solid: '#059669', soft: 'linear-gradient(135deg, rgba(5,150,105,0.12) 0%, rgba(255,255,255,0.98) 100%)', edge: '#059669' },
  { solid: '#1F3C88', soft: 'linear-gradient(135deg, rgba(31,60,136,0.12) 0%, rgba(255,255,255,0.98) 100%)', edge: '#1F3C88' },
  { solid: '#C48B32', soft: 'linear-gradient(135deg, rgba(196,139,50,0.14) 0%, rgba(255,255,255,0.98) 100%)', edge: '#C48B32' },
  { solid: '#2F6B5F', soft: 'linear-gradient(135deg, rgba(47,107,95,0.12) 0%, rgba(255,255,255,0.98) 100%)', edge: '#2F6B5F' },
];

const StatsStripSection = ({ section, sectionKey, sectionIndex, statsItems = [], renderPositionableElement }) => {
  const accent = getAccentColor(section) || '#4F46E5';
  const columns = Math.min(4, Math.max(2, Number(section.statsColumns) || 4));

  return (
    <Box component="section" sx={{ py: { xs: 1, md: 1.6 }, width: '100%' }}>
      <Stack spacing={2.8}>
        {(section.title || section.body) && (
          <Stack spacing={1} textAlign="center" sx={{ alignItems: 'center' }}>
            {section.title && <Typography sx={{ color: '#0F172A', fontWeight: 850, fontSize: { xs: '1.9rem', md: '2.7rem' }, letterSpacing: '-0.03em' }}>{section.title}</Typography>}
            {section.body && <Typography sx={{ color: '#64748B', fontSize: { xs: '0.98rem', md: '1.08rem' }, maxWidth: 760, mx: 'auto', lineHeight: 1.8 }}>{section.body}</Typography>}
          </Stack>
        )}

        <Box
          sx={{
            display: 'grid',
            gap: { xs: 1.2, md: 1.6 },
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: `repeat(${columns}, 1fr)` },
          }}
        >
          {statsItems.length ? statsItems.map((item, index) => (
            <Box key={`${sectionKey}-stat-${index}`}>
              {renderPositionableElement({
                section, index: sectionIndex, elementKey: `${sectionKey}-stat-${index}`,
                children: (() => {
                  const theme = STAT_THEMES[index % STAT_THEMES.length] || { solid: accent, soft: alpha(accent, 0.08), edge: accent };
                  return (
                  <Box
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: { xs: 2.1, md: 2.8 },
                      minHeight: { xs: 180, md: 220 },
                      borderRadius: '36px',
                      background: theme.soft,
                      border: `1px solid ${alpha(theme.solid, 0.12)}`,
                      overflow: 'hidden',
                      transition: 'transform 260ms ease, background 260ms ease',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: -28,
                        right: -24,
                        width: 96,
                        height: 96,
                        borderRadius: '6px',
                        background: alpha(theme.solid, 0.12),
                      },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 22,
                        bottom: 22,
                        width: 7,
                        borderRadius: '0 999px 999px 0',
                        background: theme.edge,
                      },
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        background: `linear-gradient(135deg, ${alpha(theme.solid, 0.16)} 0%, rgba(255,255,255,0.98) 100%)`,
                      },
                    }}
                  >
                    <Typography sx={{ color: '#0F172A', fontSize: { xs: '2.4rem', md: '3.4rem' }, fontWeight: 950, lineHeight: 1, mb: 1, letterSpacing: '-0.04em', position: 'relative', zIndex: 1 }}>
                      {item.value || ''}
                    </Typography>
                    <Typography sx={{ color: theme.solid, fontSize: { xs: '0.78rem', md: '0.84rem' }, fontWeight: 800, letterSpacing: '0.11em', textTransform: 'uppercase', textAlign: 'center', position: 'relative', zIndex: 1, maxWidth: 180 }}>
                      {item.label || ''}
                    </Typography>
                  </Box>
                  );
                })(),
              })}
            </Box>
          )) : (
            <Box sx={{ gridColumn: '1/-1', py: 4, textAlign: 'center', border: '2px dashed #CBD5E1', borderRadius: '32px' }}>
              <Typography sx={{ color: '#64748B' }}>Add school facts in the editor.</Typography>
            </Box>
          )}
        </Box>
      </Stack>
    </Box>
  );
};
export default StatsStripSection;
